<template>
  <div class="d-flex flex-column align-items-center page-wrapper">
    <img :src="require(`../brand/${this.brandConfig.assetBrand || this.brandConfig.brand}/assets/evina-img.webp`)" class="w-100">

    <div class="header-text mt-3">
      Enter the 4-digit OTP
    </div>
    <div class="sub-header-text mt-2">
      We sent a code to {{ maskedMsisdn }} via SMS.
    </div>

    <input
      ref="otpInput"
      v-model="otp"
      class="otp-input mt-4"
      inputmode="numeric"
      pattern="[0-9]*"
      maxlength="6"
      autocomplete="one-time-code"
      placeholder="0000"
      @keyup.enter="handleSubmit"
    >

    <div v-if="errorMessage" class="error-text mt-3">
      {{ errorMessage }}
    </div>

    <button
      class="accept-btn"
      :disabled="isSubmitting || !otp || otp.length < 4"
      :style="{
        backgroundColor: brandConfig.buttonColor || 'limegreen',
        color: brandConfig.buttonTextColor || '#FFFFFF'
      }"
      @click="handleSubmit">
      <span v-if="isSubmitting" class="spinner"></span>
      <span v-else>Submit</span>
    </button>

    <button
      class="resend-btn mt-3"
      :disabled="isSubmitting || isResending"
      @click="handleResend">
      <span v-if="isResending">Sending…</span>
      <span v-else>Resend OTP</span>
    </button>
  </div>
</template>

<script>
import {mapState} from "vuex";
import SafaricomService from "@/services/safaricomService";
import {triggerGTMEvent} from "@/functions/addGTM";
import getCampaignParametersFromUrl from "@/functions/getCampaignParametersFromUrl";

// The response contract from /validate-otp — matches the Flutter client's reading of it
// (client.battle-league-gaming-v2/lib/providers/auth_provider.dart:2121-2160): a non-empty
// sessionId means success; UsedOTP/ExpiredOTP/OTPAttemptCount classify the failure.
function classifyValidateResponse(response) {
  if (!response) return {ok: false, message: 'Validation failed'};
  if (response.sessionId) return {ok: true, sessionId: response.sessionId};
  if (response.UsedOTP === 1 || response.UsedOTP === true) {
    return {ok: false, message: 'This OTP has already been used. Please request a new one.'};
  }
  if (response.ExpiredOTP === 1 || response.ExpiredOTP === true) {
    return {ok: false, message: 'This OTP has expired. Please request a new one.'};
  }
  const attempts = Number(response.OTPAttemptCount);
  if (Number.isFinite(attempts) && attempts > 2) {
    return {ok: false, fatal: true, message: 'Too many incorrect attempts.'};
  }
  return {ok: false, message: 'Invalid OTP. Please try again.'};
}

export default {
  computed: {
    ...mapState(['brandConfig', 'msisdn']),
    maskedMsisdn() {
      if (!this.msisdn) return 'your Safaricom number';
      const s = String(this.msisdn);
      if (s.length <= 4) return s;
      return s.slice(0, s.length - 4).replace(/./g, '*') + s.slice(-4);
    }
  },
  data() {
    return {
      otp: '',
      isSubmitting: false,
      isResending: false,
      errorMessage: null
    }
  },
  methods: {
    async handleSubmit() {
      if (this.isSubmitting || !this.msisdn || !this.otp) return;
      this.isSubmitting = true;
      this.errorMessage = null;
      triggerGTMEvent('otp_submit_clicked');

      try {
        const response = await SafaricomService.validateOtp(
          this.msisdn,
          this.brandConfig.productId,
          this.otp.trim()
        );

        const verdict = classifyValidateResponse(response);

        if (verdict.ok) {
          triggerGTMEvent('otp_submit_success');
          const gameURL = new URL(this.brandConfig.gameUrl);
          gameURL.searchParams.set('msisdn', this.msisdn);
          const campaignParams = await getCampaignParametersFromUrl();
          if (campaignParams?.campaignId) gameURL.searchParams.set('cid', campaignParams.campaignId);
          if (campaignParams?.clickId) gameURL.searchParams.set('clickid', campaignParams.clickId);
          if (campaignParams?.publisherId) gameURL.searchParams.set('publisherId', campaignParams.publisherId);
          window.location.href = gameURL.toString();
          return;
        }

        triggerGTMEvent('otp_submit_failed');
        if (verdict.fatal) {
          this.$router.push({name: 'Error'});
          return;
        }
        this.errorMessage = verdict.message;
        this.otp = '';
      } catch (error) {
        triggerGTMEvent('otp_submit_error');
        this.errorMessage = error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';
      } finally {
        this.isSubmitting = false;
      }
    },
    async handleResend() {
      if (this.isResending || !this.msisdn) return;
      this.isResending = true;
      this.errorMessage = null;
      triggerGTMEvent('otp_resend_clicked');
      try {
        await SafaricomService.requestOtp(
          this.msisdn,
          this.brandConfig.productId,
          this.brandConfig.offerCode
        );
      } catch (error) {
        this.errorMessage = 'Failed to resend OTP. Please try again.';
      } finally {
        this.isResending = false;
      }
    }
  },
  mounted() {
    triggerGTMEvent('otp_page_view');
    // If somebody deep-links here without a stored MSISDN, we can't proceed.
    if (!this.msisdn) {
      this.$router.replace({name: 'Landing'});
      return;
    }
    this.$nextTick(() => this.$refs.otpInput?.focus());
  }
}
</script>

<style scoped>
.page-wrapper {
  height: 100vh;
}

.header-text {
  color: #FFFFFF;
  text-align: center;
  font-size: 1.2rem;
  font-weight: bold;
}

.sub-header-text {
  color: #FFFFFF;
  text-align: center;
  font-size: 0.8rem;
}

.otp-input {
  width: 60%;
  max-width: 260px;
  padding: 12px 16px;
  font-size: 1.6rem;
  text-align: center;
  letter-spacing: 0.5em;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  background-color: rgba(255, 255, 255, 0.08);
  color: #FFFFFF;
}

.otp-input:focus {
  outline: none;
  border-color: #FFFFFF;
  background-color: rgba(255, 255, 255, 0.12);
}

.error-text {
  color: #ff8a80;
  text-align: center;
  font-size: 0.85rem;
  padding: 0 1rem;
}

.accept-btn {
  background-color: limegreen;
  color: #FFFFFF;
}

.resend-btn {
  background-color: transparent;
  color: #FFFFFF;
  text-decoration: underline;
  padding: 8px;
  font-size: 0.9rem;
  width: auto;
  margin-top: 0.5rem;
}

button {
  padding: 15px 30px;
  font-size: 1.4rem;
  cursor: pointer;
  margin-top: 1.5rem;
  transition: background-color 0.3s, transform 0.2s;
  width: 80%;
  border: none;
}

button:hover:not(:disabled) {
  transform: translateY(-2px);
}

button:disabled {
  background-color: #9e9e9e !important;
  color: #f5f5f5 !important;
  cursor: not-allowed;
  transform: none;
  opacity: 0.7;
}

.resend-btn:disabled {
  background-color: transparent !important;
  color: #f5f5f5 !important;
  text-decoration: none;
  opacity: 0.7;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
